import { useCallback, useEffect, useMemo, useRef } from "react";
import { AgGrid, Flex, Grid, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { compareDateStrings } from "../sorting";
import { noRowsOverlayIcon } from "./styles_vanilla.css";

const QUERY_DATE_TIME_FORMAT = "MMM d, yyyy h:mma";

export type Query = {
  id: string;
  name: string;
  numberOfFields: number | null;
  isActive: boolean;
  /** Raw ISO timestamp; formatted for display via `valueGetter`. */
  lastUpdated: string | null;
  updatedByFullName: string | null;
  /** Raw ISO timestamp; formatted for display via `valueGetter`. */
  archivedAt: string | null;
  archivedByFullName: string | null;
};

const formatDateWithBy = (
  timestamp: string | null | undefined,
  by: string | null | undefined,
  formatDateWithTimezone: (date: string, format: string) => string,
): string => {
  const formatted = timestamp ? formatDateWithTimezone(timestamp, QUERY_DATE_TIME_FORMAT) : "";
  const trimmedBy = by?.trim();
  if (formatted && trimmedBy) return `${formatted} by ${trimmedBy}`;
  return formatted || trimmedBy || "";
};

type QueriesGridProps = {
  queries: Query[];
  searchValue?: string;
  viewFilter?: "active" | "archived";
  onEditQuery?: (queryId: string) => void;
};

type GridContext = {
  searchValue: string;
  viewFilter: "active" | "archived";
};

const QueriesNoRowsOverlay = (params: AgGrid.INoRowsOverlayParams<Query, GridContext>) => {
  const isArchived = params.context?.viewFilter === "archived";
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={SPACING.X_SMALL}
    >
      <Icon
        type="search"
        size="xx-large"
        color={Colors.Neutral600}
        className={noRowsOverlayIcon}
      />
      <Text variant="h4" align="center" color={Colors.Neutral700}>
        {isArchived ? "No archived queries yet" : "No queries yet"}
      </Text>
    </Flex>
  );
};

const HighlightCellRenderer = (
  params: AgGrid.ICellRendererParams<Query, string, GridContext>,
) => {
  const searchValue = params.context?.searchValue ?? "";
  return <HighlightText text={params.value ?? ""} searchValue={searchValue} />;
};

const NumberOfFieldsCellRenderer = (
  params: AgGrid.ICellRendererParams<Query, number | null>,
) => {
  const value = params.value;
  return <>{value === null || value === undefined ? "—" : value.toString()}</>;
};

const queryMatchesSearch = (query: Query, searchTerm: string): boolean => {
  return query.name.toLowerCase().includes(searchTerm.toLowerCase());
};

export const QueriesGrid = ({
  queries,
  searchValue = "",
  viewFilter = "active",
  onEditQuery,
}: QueriesGridProps) => {
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();

  const rowData = useMemo<Query[]>(() => {
    const trimmedSearch = searchValue.trim();
    if (!trimmedSearch) {
      return queries;
    }
    return queries.filter((query) => queryMatchesSearch(query, trimmedSearch));
  }, [queries, searchValue]);

  // Single column whose value/sort key swap based on `viewFilter`. Keeping a
  // stable `colId` (instead of switching the col definition between
  // `lastUpdated` and `archivedAt`) avoids an ag-grid column remount on every
  // toggle, so user-applied sort and width survive switching the filter.
  const columnDefs = useMemo<AgGrid.ColDef<Query>[]>(() => {
    const isArchivedView = viewFilter === "archived";
    return [
      {
        field: "name",
        headerName: "Query name",
        sortable: true,
        flex: 2,
        cellRenderer: HighlightCellRenderer,
      },
      {
        field: "numberOfFields",
        headerName: "Number of fields",
        sortable: true,
        flex: 1,
        cellRenderer: NumberOfFieldsCellRenderer,
      },
      {
        colId: "updatedOrArchivedAt",
        headerName: isArchivedView ? "Archived date" : "Last updated",
        sortable: true,
        flex: 1.5,
        cellRenderer: HighlightCellRenderer,
        valueGetter: (params) =>
          isArchivedView
            ? formatDateWithBy(
                params.data?.archivedAt,
                params.data?.archivedByFullName,
                formatDateWithTimezone,
              )
            : formatDateWithBy(
                params.data?.lastUpdated,
                params.data?.updatedByFullName,
                formatDateWithTimezone,
              ),
        comparator: (_, __, nodeA, nodeB) =>
          isArchivedView
            ? compareDateStrings(nodeA.data?.archivedAt, nodeB.data?.archivedAt)
            : compareDateStrings(nodeA.data?.lastUpdated, nodeB.data?.lastUpdated),
      },
    ];
  }, [viewFilter, formatDateWithTimezone]);

  const context = useMemo<GridContext>(
    () => ({
      searchValue,
      viewFilter,
    }),
    [searchValue, viewFilter],
  );

  const handleRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<Query>) => {
      if (event.data) {
        onEditQuery?.(event.data.id);
      }
    },
    [onEditQuery],
  );

  // ag-grid doesn't re-run cell renderers when only `context` changes, so a
  // search keystroke that doesn't change the filtered row set would leave
  // surviving rows displaying a stale highlight. Force a refresh on
  // searchValue change to keep the highlight overlay in sync.
  const gridApiRef = useRef<AgGrid.GridApi<Query> | null>(null);
  const handleGridReady = useCallback((params: AgGrid.GridReadyEvent<Query>) => {
    gridApiRef.current = params.api;
  }, []);
  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [searchValue]);

  const { gridOptions } = useAgGridDefaults({
    overrides: {
      domLayout: "autoHeight",
      rowHeight: 50,
      suppressContextMenu: true,
      suppressCellFocus: true,
      suppressHorizontalScroll: true,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
    },
  });

  return (
    <Grid<Query>
      rowData={rowData}
      getRowId={(params) => params.data.id}
      columnDefs={columnDefs}
      gridOptions={gridOptions}
      context={context}
      onGridReady={handleGridReady}
      onRowClicked={handleRowClicked}
      noRowsOverlayComponent={QueriesNoRowsOverlay}
      initialState={{
        sort: {
          sortModel: [{ colId: "updatedOrArchivedAt", sort: "desc" }],
        },
      }}
    />
  );
};
